<template>
  <div class="flex flex-col h-full">
    <!-- Desktop: search + "Show canceled" checkbox, one row -->
    <div v-if="!isMobile" class="flex items-center gap-2 px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
      <div class="relative flex-shrink-0 w-64">
        <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/>
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search tasks..."
          class="w-full pl-9 pr-3 py-1 text-sm bg-gray-50 dark:bg-gray-800 border border-transparent rounded-lg text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white dark:focus:bg-[#1C2128] transition-colors"
        />
      </div>

      <label class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer ml-auto flex-shrink-0">
        <input type="checkbox" :checked="store.boardShowCanceled" @change="store.setShowCanceled($event.target.checked)" class="rounded text-blue-600 focus:ring-blue-500" />
        Show canceled
      </label>
    </div>

    <!-- Mobile: full-width search, "Show canceled" as a native-style toggle row -->
    <div v-else class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700 pb-2.5">
      <div class="px-4 pt-2.5">
        <div class="relative">
          <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/>
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search tasks..."
            class="w-full pl-10 pr-3 py-2.5 text-[15px] bg-gray-100 dark:bg-gray-800 border border-transparent rounded-xl text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-[#0d1117] transition-colors"
          />
        </div>
      </div>
      <div class="flex items-center justify-between px-4 pt-3">
        <span class="text-[13px] font-medium text-gray-500 dark:text-gray-400">Show canceled</span>
        <button
          type="button"
          role="switch"
          :aria-checked="store.boardShowCanceled"
          @click="store.setShowCanceled(!store.boardShowCanceled)"
          class="relative w-11 h-6 rounded-full transition-colors flex-shrink-0"
          :class="store.boardShowCanceled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'"
        >
          <span class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform" :class="store.boardShowCanceled ? 'translate-x-5' : ''"></span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="store.boardLoading && !store.boardItems.length" class="flex-1 overflow-x-auto overflow-y-hidden snap-x snap-mandatory sm:snap-none">
      <div class="flex gap-4 h-full px-4 py-4 min-w-max animate-pulse">
        <div v-for="col in COLUMNS" :key="col.status" class="w-[85vw] sm:w-72 flex-shrink-0 snap-center sm:snap-align-none flex flex-col bg-gray-50 dark:bg-[#0d1117]/60 rounded-xl overflow-hidden">
          <div class="flex-shrink-0 flex items-center justify-between px-3 py-3">
            <div class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div class="h-3.5 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div class="h-3 w-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
          <div class="flex-1 px-2 pb-2 space-y-2">
            <div v-for="i in skeletonCounts[col.status]" :key="i" class="h-[76px] bg-white dark:bg-[#1C2128] rounded-lg border border-gray-100 dark:border-gray-700"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="store.boardError" class="flex-1 flex flex-col items-center justify-center py-32 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.boardError }}</p>
      <button @click="retry" class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">Try again</button>
    </div>

    <!-- Loaded -->
    <div v-else class="flex-1 overflow-x-auto overflow-y-hidden snap-x snap-mandatory sm:snap-none">
      <div class="flex gap-4 h-full px-4 py-4 min-w-max">
        <div
          v-for="col in COLUMNS"
          :key="col.status"
          class="w-[85vw] sm:w-72 flex-shrink-0 snap-center sm:snap-align-none flex flex-col bg-gray-50 dark:bg-[#0d1117]/60 rounded-xl overflow-hidden"
        >
          <div class="flex-shrink-0 flex items-center justify-between px-3 py-3">
            <span class="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
              <span class="w-2 h-2 rounded-full flex-shrink-0" :class="col.dot"></span>
              {{ col.label }}
            </span>
            <span class="text-xs font-medium text-gray-400 dark:text-gray-500">{{ visibleCount(col.status) }}</span>
          </div>

          <draggable
            v-model="localColumns[col.status]"
            :group="groupFor(col.status)"
            item-key="id"
            :animation="220"
            easing="cubic-bezier(0.2, 0, 0.2, 1)"
            ghost-class="task-drag-ghost"
            chosen-class="task-drag-chosen"
            drag-class="task-drag-active"
            class="flex-1 overflow-y-auto px-2 pb-2 space-y-2 min-h-[3rem]"
            @change="onChange(col.status, $event)"
          >
            <template #item="{ element }">
              <TaskCard v-show="matchesSearch(element)" :task="element" @open="openTask" />
            </template>
          </draggable>

          <div class="px-2 pb-2 flex-shrink-0">
            <p v-if="!visibleCount(col.status) && addingTo !== col.status" class="text-center text-xs text-gray-300 dark:text-gray-600 py-4">
              {{ searchQuery.trim() ? 'No matches' : 'No tasks' }}
            </p>

            <!-- Quick add -->
            <div v-if="addingTo === col.status" class="bg-white dark:bg-[#1C2128] border border-blue-200 dark:border-blue-800 rounded-lg p-2">
              <textarea
                ref="quickAddInput"
                v-model="newTitle"
                rows="2"
                placeholder="Task title"
                class="w-full text-sm text-gray-800 dark:text-gray-100 bg-transparent border-0 rounded-md resize-none placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-transparent"
                @keydown.enter.prevent="confirmAdd(col.status)"
                @keydown.esc="cancelAdd"
              ></textarea>
              <div class="flex items-center gap-2 mt-1">
                <button
                  type="button"
                  @click="confirmAdd(col.status)"
                  :disabled="!newTitle.trim() || addSaving"
                  class="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-md transition-colors"
                >{{ addSaving ? 'Adding...' : 'Add' }}</button>
                <button type="button" @click="cancelAdd" class="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
            <!-- No quick-add in Canceled — creating a task that starts out
                 canceled isn't a thing anyone wants, and canceled is
                 terminal so it couldn't be moved out afterwards. -->
            <button
              v-else-if="col.status !== 'canceled'"
              type="button"
              @click="startAdd(col.status)"
              class="w-full flex items-center gap-1.5 px-2 py-2.5 sm:py-1.5 text-[13px] sm:text-xs font-medium text-gray-400 sm:hover:text-gray-600 dark:sm:hover:text-gray-300 sm:hover:bg-gray-100 dark:sm:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Add task
            </button>
          </div>
        </div>
      </div>
    </div>

    <FlashNotifications ref="flash" />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import draggable from 'vuedraggable'
import { useTasksStore } from 'stores/tasks.js'
import { getCachedBoardColumnCount } from 'composables/useTasksBoardCountCache.js'
import TaskCard from './TaskCard.vue'
import FlashNotifications from 'controllers/vue_components/flash_toast'

// Dot colors mirror STATUS_META in TaskDetailPage.vue's Status dropdown —
// same status, same color, wherever it shows up in the Tasks UI.
const BASE_COLUMNS = [
  { status: 'pending', label: 'Pending', dot: 'bg-gray-400' },
  { status: 'started', label: 'Started', dot: 'bg-blue-500' },
  { status: 'paused', label: 'Paused', dot: 'bg-amber-500' },
  { status: 'completed', label: 'Completed', dot: 'bg-green-500' },
]
// Canceled is not a standing column — it only appears while "Show canceled"
// is on, which is what that toggle now actually does. Before this it
// refetched with show_canceled=true and nothing changed on screen, because
// there was no column for a canceled card to land in.
const CANCELED_COLUMN = { status: 'canceled', label: 'Canceled', dot: 'bg-red-400' }

const props = defineProps({
  // See TasksChecklist.vue's identical prop for why — TasksPage.vue mounts
  // a desktop and a mobile instance side by side, and this picks which
  // markup branch each instance renders.
  variant: { type: String, default: 'desktop' },
})
const isMobile = computed(() => props.variant === 'mobile')

const store = useTasksStore()
const router = useRouter()
const flash = ref(null)

const COLUMNS = computed(() =>
  store.boardShowCanceled ? [...BASE_COLUMNS, CANCELED_COLUMN] : BASE_COLUMNS
)

// Cards can be dropped into Canceled but never dragged back out —
// Task::ALLOWED_TRANSITIONS makes canceled a one-way terminal state, so a
// drag out of it would fail server-side and snap back with no explanation.
// `pull: false` stops the drag from being offered in the first place.
function groupFor(status) {
  return status === 'canceled'
    ? { name: 'board-tasks', pull: false, put: true }
    : { name: 'board-tasks', pull: true, put: true }
}

// See docs/skeleton_count_caching.md — read synchronously at setup so the
// first paint already shows roughly the right number of cards per column,
// not a generic guess.
const skeletonCounts = computed(() => {
  const counts = {}
  COLUMNS.value.forEach((col) => { counts[col.status] = Math.min(getCachedBoardColumnCount(col.status), 12) })
  return counts
})

onMounted(() => store.fetchBoardIfStale())

function openTask(task) {
  router.push(`/tasks/${task.id}`)
}

function retry() {
  store.invalidate()
  store.fetchBoard()
}

// ── Local per-column arrays for <draggable> ──────────────────────────────
// vuedraggable needs a plain mutable array per list (it reorders in place
// as you drag). store.boardColumns is a getter derived from boardItems, so
// it can't be v-model'd directly — mirror it into local reactive arrays,
// re-synced whenever the underlying store data changes (after a fetch, a
// quick-add, or a server-confirmed reorder via patchLocal).
// `canceled` is always a key here even while its column is hidden — the
// watcher below just fills it with an empty array in that case, which keeps
// this a fixed shape rather than something that has to be added/removed in
// step with the toggle.
const localColumns = reactive({ pending: [], started: [], paused: [], completed: [], canceled: [] })

watch(() => store.boardColumns, (cols) => {
  Object.keys(localColumns).forEach((status) => {
    localColumns[status] = [...(cols[status] || [])]
  })
}, { immediate: true })

// Client-side title search. Deliberately doesn't filter localColumns[status]
// itself — that array is vuedraggable's live v-model, and removing entries
// from it would desync drag-and-drop position math against the hidden
// cards. Non-matching cards are hidden with v-show (see the #item slot
// below) instead, which never touches the underlying array or its indices,
// so dragging still works normally while a search is active.
const searchQuery = ref('')
function matchesSearch(task) {
  const q = searchQuery.value.trim().toLowerCase()
  return !q || task.title.toLowerCase().includes(q)
}
function visibleCount(status) {
  return localColumns[status].filter(matchesSearch).length
}

// ── Quick add (per column, no navigation — matches Trello/ClickUp's
// "+ Add card" inline row; open the created task to add a description,
// recurrence, priority, etc.). `startAdd` is also called by TasksPage.vue's
// header "New task" button (defaults to the Pending column) — exposed below.
const addingTo = ref(null)
const newTitle = ref('')
const addSaving = ref(false)
const quickAddInput = ref(null)

// The quick-add textarea's `ref` sits inside the column v-for, so Vue
// compiles it with `ref_for: true` and fills this ref with an *array* of
// elements — even though the `v-if="addingTo === col.status"` beside it
// means only ever one is actually mounted. Calling `.focus()` straight on
// the ref throws (an array has no focus), and `?.` does not catch that: it
// only guards the ref being null. So the row opened and never took focus,
// which also broke confirmAdd's stay-open-for-rapid-multi-add refocus.
function focusQuickAdd() {
  const el = quickAddInput.value
  const target = Array.isArray(el) ? el[0] : el
  target?.focus()
}

async function startAdd(status = 'pending') {
  addingTo.value = status
  newTitle.value = ''
  await nextTick()
  focusQuickAdd()
}

function cancelAdd() {
  addingTo.value = null
  newTitle.value = ''
}

async function confirmAdd(status) {
  const title = newTitle.value.trim()
  if (!title || addSaving.value) return

  addSaving.value = true
  try {
    const created = await store.createTask({ title, status })
    if (created) {
      newTitle.value = ''
      await store.fetchBoard()
      await nextTick()
      focusQuickAdd()
    }
  } finally {
    addSaving.value = false
  }
}

// ── Drag-and-drop ─────────────────────────────────────────────────────────
// localColumns[status] already reflects the post-drop order by the time
// `change` fires (vuedraggable's v-model updated it for us). Re-derive
// sequential positions for whichever column just received/reordered a card
// and send only the ones that actually moved — the source column needs no
// update at all (losing a card doesn't change the relative order of the
// ones left behind).
//
// All of them go in one request. The earlier version called
// store.reorder(task, status, idx) per card inside this loop, unawaited: a
// drop at the top of a ten-card column fired ten parallel PATCHes, each
// response patching the store and re-triggering the boardColumns watcher
// below while the others were still open, and any one failure refetched the
// whole board mid-flight. See store.reorderColumn.
async function syncColumnPositions(status) {
  const placements = []
  localColumns[status].forEach((task, idx) => {
    if (task.position !== idx || task.status !== status) {
      placements.push({ id: task.id, status, position: idx })
    }
  })
  if (!placements.length) return

  const ok = await store.reorderColumn(placements)
  if (!ok) flash.value?.show("Couldn't move the task — please try again.", 'alert')
}

function onChange(status, evt) {
  if (evt.added || evt.moved) syncColumnPositions(status)
}

defineExpose({ startAdd })
</script>

<style scoped>
/* The gap left behind at the drop target while dragging.

   Compounded with `.task-card` on purpose. TaskCard.vue paints every card with
   a wash of its own color as a `background-image`, and in a scoped stylesheet
   that is `.task-card[data-v-child]` — exactly as specific as a bare
   `:deep(.task-drag-ghost)`, which compiles to
   `[data-v-parent] .task-drag-ghost`. The `background` shorthand below has to
   win in order to reset that image and show the blue drop gap, and a
   specificity tie between two components' stylesheets is settled by whichever
   is injected last. The extra class makes this strictly more specific, and so
   independent of bundler ordering. (The card's hover rule needs no such fix —
   it already excludes the drag classes with `:not()`.) */
:deep(.task-card.task-drag-ghost) {
  opacity: 0.5;
  background: rgb(239 246 255);
  border: 2px dashed rgb(96 165 250);
  border-radius: 0.5rem;
}
:global(.dark) :deep(.task-card.task-drag-ghost) {
  background: rgb(30 58 95 / 0.4);
  border-color: rgb(59 130 246);
}
/* The original card, while it's the one being dragged */
:deep(.task-drag-chosen) {
  cursor: grabbing;
}
/* The clone that follows the cursor */
:deep(.task-drag-active) {
  box-shadow: 0 10px 25px -5px rgb(0 0 0 / 0.15), 0 8px 10px -6px rgb(0 0 0 / 0.1);
  transform: rotate(1.5deg);
}
</style>
